import {
  Sprout,
  TrendingUp,
  Flame,
  Waves,
  Archive,
  ShoppingBag,
  Wifi,
  Car,
  Bath,
  ShowerHead,
  Shirt,
  Lock,
  Coffee,
  Accessibility,
  ShieldCheck,
  Camera,
  Baby,
  Languages,
  HeartPulse,
  // Ícones adicionais disponíveis para selos customizados (picker).
  Sun,
  Sunrise,
  Wind,
  Umbrella,
  Anchor,
  Sailboat,
  Ship,
  LifeBuoy,
  Fish,
  Droplet,
  Thermometer,
  CloudSun,
  Snowflake,
  Mountain,
  TreePalm,
  Tent,
  Compass,
  Map,
  Medal,
  Trophy,
  Award,
  Star,
  Sparkles,
  Zap,
  Flag,
  Target,
  Gift,
  Heart,
  Smile,
  ThumbsUp,
  BadgeCheck,
  Users,
  GraduationCap,
  BookOpen,
  Dumbbell,
  Bike,
  Footprints,
  Hand,
  Glasses,
  Backpack,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Music,
  Video,
  Utensils,
  Wine,
  Dog,
  type LucideIcon,
} from 'lucide-react'

export type SchoolFeatureGroup = 'level' | 'amenity'

export interface SchoolFeature {
  key: string
  label: string
  icon: LucideIcon
  group: SchoolFeatureGroup
}

// Selo/facilidade criado livremente pela escola. `icon` é o nome (chave) de um
// ícone do FEATURE_ICON_REGISTRY. `id` é gerado no cliente (uuid/random).
export interface CustomSchoolFeature {
  id: string
  group: SchoolFeatureGroup
  label: string
  icon: string
}

// Catálogo de selos que o admin marca no configurador e que viram ícones na
// página pública. Chaves são estáveis (salvas no banco); labels/ícones podem mudar.
export const SCHOOL_FEATURES: SchoolFeature[] = [
  // Níveis de aula
  {
    key: 'level_beginner',
    label: 'Aula para iniciantes',
    icon: Sprout,
    group: 'level',
  },
  {
    key: 'level_intermediate',
    label: 'Aula intermediária',
    icon: TrendingUp,
    group: 'level',
  },
  {
    key: 'level_advanced',
    label: 'Aula avançada',
    icon: Flame,
    group: 'level',
  },

  // Facilidades / estrutura
  {
    key: 'board_rental',
    label: 'Aluguel de pranchas',
    icon: Waves,
    group: 'amenity',
  },
  {
    key: 'board_storage',
    label: 'Guarda de pranchas',
    icon: Archive,
    group: 'amenity',
  },
  { key: 'shop', label: 'Vende material', icon: ShoppingBag, group: 'amenity' },
  { key: 'wifi', label: 'Wi-Fi', icon: Wifi, group: 'amenity' },
  { key: 'parking', label: 'Estacionamento', icon: Car, group: 'amenity' },
  { key: 'restroom', label: 'Banheiro', icon: Bath, group: 'amenity' },
  { key: 'shower', label: 'Chuveiro', icon: ShowerHead, group: 'amenity' },
  { key: 'changing_room', label: 'Vestiário', icon: Shirt, group: 'amenity' },
  { key: 'lockers', label: 'Armários', icon: Lock, group: 'amenity' },
  { key: 'snacks', label: 'Lanchonete', icon: Coffee, group: 'amenity' },
  {
    key: 'accessibility',
    label: 'Acessibilidade',
    icon: Accessibility,
    group: 'amenity',
  },
  {
    key: 'insurance',
    label: 'Segurança / seguro',
    icon: ShieldCheck,
    group: 'amenity',
  },
  { key: 'photos', label: 'Fotos das aulas', icon: Camera, group: 'amenity' },
  { key: 'kids', label: 'Aulas para crianças', icon: Baby, group: 'amenity' },
  {
    key: 'english',
    label: 'Atendimento em inglês',
    icon: Languages,
    group: 'amenity',
  },
  {
    key: 'first_aid',
    label: 'Primeiros socorros',
    icon: HeartPulse,
    group: 'amenity',
  },
]

export const SCHOOL_FEATURE_MAP: Record<string, SchoolFeature> =
  Object.fromEntries(SCHOOL_FEATURES.map((f) => [f.key, f]))

export const featuresByGroup = (group: SchoolFeatureGroup): SchoolFeature[] =>
  SCHOOL_FEATURES.filter((f) => f.group === group)

// Catálogo de ícones que a escola pode escolher ao criar um selo custom.
// A chave (string) é o que fica salvo no banco; o componente é resolvido na hora.
export const FEATURE_ICON_REGISTRY: Record<string, LucideIcon> = {
  Sprout,
  TrendingUp,
  Flame,
  Waves,
  Sun,
  Sunrise,
  Wind,
  Umbrella,
  Anchor,
  Sailboat,
  Ship,
  LifeBuoy,
  Fish,
  Droplet,
  Thermometer,
  CloudSun,
  Snowflake,
  Mountain,
  TreePalm,
  Tent,
  Compass,
  Map,
  MapPin,
  Medal,
  Trophy,
  Award,
  Star,
  Sparkles,
  Zap,
  Flag,
  Target,
  Gift,
  Heart,
  HeartPulse,
  Smile,
  ThumbsUp,
  BadgeCheck,
  ShieldCheck,
  Users,
  GraduationCap,
  BookOpen,
  Dumbbell,
  Bike,
  Footprints,
  Hand,
  Glasses,
  Backpack,
  Calendar,
  Clock,
  Phone,
  Wifi,
  Car,
  ShoppingBag,
  Coffee,
  Utensils,
  Wine,
  Bath,
  ShowerHead,
  Shirt,
  Lock,
  Archive,
  Camera,
  Video,
  Music,
  Baby,
  Dog,
  Accessibility,
  Languages,
}

// Nomes disponíveis no seletor de ícones.
export const FEATURE_ICON_NAMES: string[] = Object.keys(FEATURE_ICON_REGISTRY)

// Ícone padrão quando o nome salvo não existe (ex.: ícone renomeado/removido).
export const DEFAULT_FEATURE_ICON: LucideIcon = Sparkles

export const resolveFeatureIcon = (name?: string | null): LucideIcon =>
  (name && FEATURE_ICON_REGISTRY[name]) || DEFAULT_FEATURE_ICON
